import { useState, useCallback, useEffect, useMemo } from 'react';
import { puntiTorneo, nessunoHaGiocato, POSTO_MUTO } from '../../utils/tournament-helpers';
import { useTranslation } from 'react-i18next';
import { etichettePattaBloccata } from '../../utils/chiave-rifiuto-patta';
import { ChevronRight, Eye, List, MessageSquare, Trophy } from 'lucide-react';
import { useTournamentStore } from '../../store/tournament-store';
import { useChatStore } from '../../store/chat-store';
import { useGameStore } from '../../store/game-store';
import { useAuthStore } from '../../store/auth-store';
import type { Move } from '@draughts/shared';
import { Color, GameStatus, resolveTimeControl, toGameVariant, isRoundBasedFormat, ballotNumber } from '@draughts/shared';
import { getRatingForGame } from '../../utils/rating';
import GameScreen from '../game/GameScreen';
import type { MatchFoundData, MobilePanel } from '../game/GameScreen';
import { useGuardiaSincronia } from '../../hooks/useGuardiaSincronia';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { DRAW_DECLINE_NOTICE_MS } from '../../constants/timing';
import GameSidebarTabs from '../game/GameSidebarTabs';
import FullscreenPanel from '../game/FullscreenPanel';
import ClassificaInPartita from './ClassificaInPartita';
import { useIsMobile } from '../../hooks/useIsMobile';
import MoveList from '../game/MoveList';
import ChatPanel from '../online/ChatPanel';
import SpectatorsPanel from '../game/SpectatorsPanel';
import { useGameSpectators } from '../../store/spectators-store';
import GameEndOverlay from '../game/GameEndOverlay';
import AvvisoErrore from '../online/AvvisoErrore';
import DrawnOpeningBanner from '../online/DrawnOpeningBanner';
import { endReasonText } from '../../utils/end-reason-text';
import { ARENA_SCORING, SWISS_SCORING } from '../../types/tournament';
import './Tournament.css';
import './TournamentGame.css';

interface TournamentGameProps {
  onGameEnd: () => void;
}

function TournamentGame({ onGameEnd }: TournamentGameProps) {
  const { t } = useTranslation();
  const resyncPartita = useMultiplayerStore((s) => s.resyncGame);
  /*
   * UN SELETTORE PER CAMPO, e non `useTournamentStore()` nudo.
   *
   * Destrutturare l'intero store significa sottoscriversi a OGNI sua modifica.
   * Fra quelle c'e' `tournament:list-update`, che e' un broadcast GLOBALE: il
   * suo gestore rifa' l'elenco dei tornei con un `.map` senza condizione, cioe'
   * l'identita' cambia sempre, e ogni volta ridisegnava questa scacchiera, il
   * suo orologio, la lista mosse e la chat — di una partita che non era
   * cambiata di una virgola. Arriva anche la classifica dello svizzero, e in
   * arena la stessa cosa succedeva ogni due secondi.
   *
   * `ArenaGame.tsx` ha gia' questa forma, col suo perche' scritto accanto: era
   * rimasto fuori solo questo file.
   */
  const status = useTournamentStore((s) => s.status);
  const currentTournament = useTournamentStore((s) => s.currentTournament);
  const boardState = useTournamentStore((s) => s.boardState);
  const playerColor = useTournamentStore((s) => s.playerColor);
  const opponent = useTournamentStore((s) => s.opponent);
  const gameResult = useTournamentStore((s) => s.gameResult);
  const gameEndReason = useTournamentStore((s) => s.gameEndReason);
  const matchFoundData = useTournamentStore((s) => s.matchFoundData);
  const aperturaDellaPartita = useTournamentStore((s) => s.aperturaDellaPartita);
  const mancheDellaPartita = useTournamentStore((s) => s.mancheDellaPartita);
  const mancheTotaliDellaPartita = useTournamentStore((s) => s.mancheTotaliDellaPartita);
  const myParticipant = useTournamentStore((s) => s.myParticipant);
  const myRank = useTournamentStore((s) => s.myRank);
  const clockState = useTournamentStore((s) => s.clockState);
  const currentRound = useTournamentStore((s) => s.currentRound);
  const totalRounds = useTournamentStore((s) => s.totalRounds);
  const tournamentFormat = useTournamentStore((s) => s.tournamentFormat);
  const currentGameId = useTournamentStore((s) => s.currentGameId);
  const isOpponentDisconnected = useTournamentStore((s) => s.isOpponentDisconnected);
  const graceTimeRemaining = useTournamentStore((s) => s.graceTimeRemaining);
  const opponentMaiArrivato = useTournamentStore((s) => s.opponentMaiArrivato);
  const opponentDecideLOrologio = useTournamentStore((s) => s.opponentDecideLOrologio);
  const ratingChange = useTournamentStore((s) => s.ratingChange);
  const drawOffered = useTournamentStore((s) => s.drawOffered);
  const opponentOfferedDraw = useTournamentStore((s) => s.opponentOfferedDraw);
  const drawDeclinedNotice = useTournamentStore((s) => s.drawDeclinedNotice);
  const drawOffersLeft = useTournamentStore((s) => s.drawOffersLeft);
  const drawMustMove = useTournamentStore((s) => s.drawMustMove);
  const error = useTournamentStore((s) => s.error);
  const standings = useTournamentStore((s) => s.standings);
  /* Le azioni sono stabili per costruzione in zustand: si leggono una volta e
     non fanno mai scattare un render. */
  const makeMove = useTournamentStore((s) => s.makeMove);
  const resign = useTournamentStore((s) => s.resign);
  const confirmMatch = useTournamentStore((s) => s.confirmMatch);
  const tickClock = useTournamentStore((s) => s.tickClock);
  const offerDraw = useTournamentStore((s) => s.offerDraw);
  const acceptDraw = useTournamentStore((s) => s.acceptDraw);
  const setError = useTournamentStore((s) => s.setError);
  const declineDrawAction = useTournamentStore((s) => s.declineDraw);
  const mossaInVolo = useTournamentStore((s) => s.preMoveBoardState);
  const clockTimeControl = useTournamentStore((s) => s.timeControl);
  const pattaBloccata = etichettePattaBloccata(drawOffersLeft, drawMustMove);

  const currentUser = useAuthStore((state) => state.user);

  // Chat unread badge + active flag for the mobile full-screen chat overlay.
  const unreadCount = useChatStore((state) => state.unreadCount);
  const { count: spectatorCount } = useGameSpectators();
  const setChatActive = useChatStore((state) => state.setActive);

  // Sync to local game store for rendering
  const syncToLocalStore = useGameStore((state) => state.syncFromExternal);

  const currentTurn = boardState?.currentTurn ?? Color.WHITE;
  const gameStatus = boardState?.status ?? GameStatus.PLAYING;

  const [showResultOverlay, setShowResultOverlay] = useState(false);

  /*
   * L'azione si legge con un SELETTORE, non con `useGameStore.getState()`.
   *
   * I test di questi tre wrapper montano un finto store che è solo un selettore
   * (`useGameStore(selector)`), senza `.getState`: chiamarlo qui fa cadere il
   * componente in prova — cioè un difetto che si scopre solo dopo. È una lezione
   * già pagata in questo repo.
   */
  const enterOnlineGame = useGameStore((s) => s.enterOnlineGame);

  // Tournament play is player-vs-player: reset the shared game store to 'pvp' so a
  // leftover AI mode from an earlier local game (gameMode is never reset when
  // switching AI → online) can't block review navigation or suppress the go-live
  // blink. Board state itself comes from the server via syncFromExternal.
  useEffect(() => {
    enterOnlineGame();
  }, []);

  // Auto-dismiss della pastiglia "l'avversario ha rifiutato la patta". Stessa
  // durata e stessa ragione della partita online (vedi OnlineGame).
  useEffect(() => {
    if (!drawDeclinedNotice) return;
    const id = window.setTimeout(
      () => useTournamentStore.setState({ drawDeclinedNotice: false }),
      DRAW_DECLINE_NOTICE_MS,
    );
    return () => window.clearTimeout(id);
  }, [drawDeclinedNotice]);

  // Sync board state when it changes
  useEffect(() => {
    if (boardState && syncToLocalStore) {
      syncToLocalStore(boardState);
    }
  }, [boardState, syncToLocalStore]);

  const isOurTurn =
    (playerColor === 'white' && currentTurn === Color.WHITE) ||
    (playerColor === 'black' && currentTurn === Color.BLACK);

  const isGameOver = gameStatus !== GameStatus.PLAYING || gameResult !== null;

  /* La stessa sentinella dell'online: una damiera ferma in stato `'playing'`
     non sembra un guasto, e senza qualcuno che se ne accorga non si esce piu'.
     Il resync passa dal multiplayer-store, che e' l'unica porta di rientro e
     delega a questo store il risultato. */
  useGuardiaSincronia({
    attiva: status === 'playing' && !isGameOver,
    turnoDamiera: boardState ? (boardState.currentTurn === Color.WHITE ? 'white' : 'black') : null,
    turnoOrologio: clockState?.activePlayer ?? null,
    mossaInVolo: mossaInVolo != null,
    resync: resyncPartita,
    contesto: 'torneo',
  });

  // Debug logging
  useEffect(() => {
  }, [status, playerColor, boardState, gameResult, isGameOver, matchFoundData]);

  // Handle move from local board
  const handleMove = useCallback(
    (move: Move) => {
      if (isOurTurn && !isGameOver) {
        makeMove(move);
      }
    },
    [isOurTurn, isGameOver, makeMove]
  );

  const isWin = gameResult === playerColor;
  const isDraw = gameResult === 'draw';
  const isLoss = gameResult !== null && !isWin && !isDraw;

  const getTurnText = () => {
    if (isGameOver) {
      /*
       * Stessa guardia di `OnlineGame`/`ArenaGame`. Questa schermata calcolava
       * già `isLoss` distinguendo il `gameResult` nullo (riga sopra) e poi non
       * lo usava: il ramo finale vinceva per esclusione e il vincitore leggeva
       * «Hai Perso!» per il tempo di una transazione di fine partita.
       */
      if (!gameResult) return t('game.result.gameOver');
      if (isDraw) return t('game.result.draw');
      if (isWin) return t('game.result.youWin');
      return t('game.result.youLose');
    }
    if (isOurTurn) return t('game.turn.yourTurn');
    return t('game.turn.opponentTurn');
  };

  /* A turni, non «svizzero»: nel girone i punti sono gli stessi dello svizzero
     (`SWISS_SCORING`, oggi 2 / 1 / 0) e le serie dell'arena non esistono. */
  const aTurni = isRoundBasedFormat(tournamentFormat);

  const getPointsEarned = (): number => {
    if (!gameResult) return 0;

    if (aTurni) {
      if (isWin) return SWISS_SCORING.WIN;
      if (isDraw) return SWISS_SCORING.DRAW;
      return SWISS_SCORING.LOSS;
    }

    /* Due rami che ora coincidono nei numeri, e restano due: `ARENA_SCORING` e
       `SWISS_SCORING` sono due scale che si somigliano, non la stessa scala. */
    if (isWin) return ARENA_SCORING.WIN;
    if (isDraw) return ARENA_SCORING.DRAW;
    return ARENA_SCORING.LOSS;
  };

  const getResultClass = (): string => {
    if (isWin) return 'win';
    if (isDraw) return 'draw';
    if (isLoss) return 'lose';
    return '';
  };

  // Reset overlay when a new game starts (new round)
  useEffect(() => {
    setShowResultOverlay(false);
  }, [currentGameId]);

  /*
   * Il riepilogo resta finché non si preme «Continua».
   *
   * C'era un conto alla rovescia di cinque secondi che allo zero chiamava
   * `onGameEnd()` da solo: portava via il risultato mentre lo si stava
   * leggendo. Come in arena, adesso a chiudere è il bottone — l'unico modo per
   * uscire di qui.
   */
  useEffect(() => {
    if (isGameOver && gameResult !== null && !showResultOverlay) {
      setShowResultOverlay(true);
    }
  }, [isGameOver, gameResult, showResultOverlay]);

  const handleContinue = useCallback(() => {
    onGameEnd();
  }, [onGameEnd]);

  const handleResign = useCallback(() => {
    resign();
  }, [resign]);

  // Build match found data for GameScreen overlay
  const matchOverlay: MatchFoundData | null =
    status === 'match-found' && matchFoundData
      ? {
          opponentUsername: matchFoundData.opponentUsername,
          opponentRating: matchFoundData.opponentRating,
          opponentAvatar: matchFoundData.opponentAvatar,
          assignedColor: matchFoundData.color,
          forcedOpening: matchFoundData.forcedOpening,
        }
      : null;

  // Player info
  const color = playerColor ?? 'white';
  /*
   * Posto e punti dell'AVVERSARIO, accanto al suo nome.
   *
   * L'intestazione diceva solo i miei. In un torneo a turni sapere contro chi
   * si gioca vuol dire anche sapere dove sta in classifica — chi insegue chi,
   * chi si gioca il podio. I dati arrivano gia' con la classifica via socket;
   * senza una riga per lui (classifica non ancora arrivata) non si mostra
   * niente invece di un posto inventato.
   */
  const rigaAvversario = aTurni && opponent?.id
    ? (standings ?? []).find((r) => r.userId === opponent.id) ?? null
    : null;
  /*
   * Al PRIMO TURNO il posto non c'e' ancora: finche' nessuno ha chiuso una
   * partita il server numera tutti primi, e questi due gettoni annunciavano
   * «#1» a chiunque — il mio e quello dell'avversario. I punti restano, che
   * sono veri; il posto diventa un trattino. Vedi `nessunoHaGiocato`.
   */
  const postoMuto = nessunoHaGiocato(standings ?? []);
  const topPlayer = useMemo(() => ({
    /* `||` e non `??`: una stringa VUOTA non e' un nome, e col nullish passava
       dritta disegnando un cartellino in bianco. Come fanno già la schermata
       online e le liste dello storico. */
    username: opponent?.username || t('common.anonymous'),
    rating: opponent?.rating,
    avatar: opponent?.avatar ?? null,
    profileUsername: opponent?.username ?? null,
    nameBadge: rigaAvversario ? (
      <span className="tournament-game-opponent-standing" data-testid="posto-avversario">
        {postoMuto ? POSTO_MUTO : `#${rigaAvversario.rank}`} · {puntiTorneo(rigaAvversario.points)} {t('tournament.lobby.pts')}
      </span>
    ) : undefined,
  }), [t, opponent, rigaAvversario, postoMuto]);
  const bottomPlayer = useMemo(() => ({
    username: currentUser?.username ?? t('common.you'),
    rating: getRatingForGame(currentUser, currentTournament?.variant ?? '', currentTournament?.timeControl ?? ''),
    avatar: currentUser?.avatar ?? null,
    country: currentUser?.country ?? null,
    profileUsername: currentUser?.username ?? null,
  }), [t, currentUser, currentTournament?.variant, currentTournament?.timeControl]);

  /*
   * LA CLASSIFICA DURANTE LA PARTITA.
   *
   * Si apre toccando il punteggio dell'intestazione, che c'era gia' e diceva
   * gia' «punti · posto»: e' il posto dove la domanda «come sto andando?» ha
   * gia' mezza risposta. Non e' un quarto bottone nella barra in basso perche'
   * a 360px la barra e' piena — Mosse, tre frecce, Chat, occhio: un altro
   * bottone lascerebbe alla Chat una quindicina di pixel.
   *
   * Su mobile apre il pannello a tutto schermo (lo stesso di Mosse e Chat); su
   * desktop seleziona la scheda con la coppa nella colonna laterale, che c'e'
   * comunque e si puo' aprire anche da li'.
   *
   * L'elemento e' creato UNA volta: la classifica legge lo store da se', e un
   * elemento stabile non fa ridisegnare la colonna a ogni tick dell'orologio.
   */
  const isMobile = useIsMobile();
  const classifica = useMemo(() => <ClassificaInPartita />, []);
  const [classificaAperta, setClassificaAperta] = useState(false);
  const [richiestaClassifica, setRichiestaClassifica] = useState(0);
  const apriClassifica = () => {
    if (isMobile) setClassificaAperta(true);
    else setRichiestaClassifica((n) => n + 1);
  };
  // Da mobile a desktop col pannello aperto (rotazione, finestra allargata):
  // il pannello si chiude, e al ritorno su mobile non si riapre da solo.
  useEffect(() => {
    if (!isMobile) setClassificaAperta(false);
  }, [isMobile]);

  const punteggio = myParticipant
    ? `${puntiTorneo(myParticipant.points)} ${t('tournament.lobby.pts')} · ${postoMuto || !myRank ? POSTO_MUTO : `#${myRank}`}`
    : '';

  /*
   * IL TORNEO DENTRO LA RIGA DELL'INTESTAZIONE, non in una striscia sopra.
   *
   * Prima era `headerSlot`: un blocco largo 500px FUORI dalla griglia,
   * `flex-shrink: 0` sopra un `.gs-grid` che prende `flex: 1` — quindi i suoi
   * ~73px di altezza li pagava la damiera, e in torneo era piu' piccola che in
   * una partita normale. E' il difetto da cui nasce questo lavoro. Di piu':
   * stando fuori dalla griglia non era allineata con la colonna della damiera
   * (`.gs-header-slot` non aveva ne' padding ne' tetto, la striscia nemmeno un
   * `margin-inline: auto`), quindi stava appiccicata al bordo sinistro.
   *
   * Qui sono celle come le altre: stessa riga, stessa larghezza della colonna,
   * nessuna altezza in piu'. E' la strada che l'arena ha gia' fatto per le
   * stesse due ragioni, vedi `ArenaHeaderCells`.
   */
  const celleTorneo = (
    <>
      {/* Il testo in un elemento suo: la cella e' un contenitore flex, e su un
          contenitore flex `text-overflow` non mette i puntini. Il `title` porta
          il nome intero, che su schermo stretto si tronca — e la cella e' quella
          che CEDE lo spazio: vedi `.tournament-game-name-cell`. */}
      <span className="game-header__cell tournament-game-name-cell" data-testid="intestazione-torneo" title={currentTournament?.name}>
        <Trophy size={14} aria-hidden="true" className="tournament-game-icon" />
        <span className="tournament-game-name">{currentTournament?.name}</span>
      </span>
      {aTurni && currentRound > 0 && (
        <>
          <span className="game-header__divider" aria-hidden="true" />
          <span className="game-header__cell tournament-game-round">
            {t('tournament.swiss.roundOf', { current: currentRound, total: totalRounds })}
          </span>
        </>
      )}
      {myParticipant && (
        <>
          <span className="game-header__divider" aria-hidden="true" />
          {/* Un bottone, ma vestito come la scritta che era: stesso colore,
              stesso carattere. La freccetta dice che si tocca; i 44px d'area di
              tocco li da' `--tap` (vedi GameHeader.css), che alza la riga invece
              di farli sporgere fuori dal suo ritaglio — qui i margini negativi
              di prima verrebbero tagliati. Il nome accessibile CONTIENE il testo
              visibile, cosi' chi comanda a voce «tocca 3 punti» lo trova. */}
          <button
            type="button"
            className="game-header__cell game-header__cell--tap game-header__cell--apri tournament-game-score tournament-game-score--apri"
            onClick={apriClassifica}
            aria-label={`${t('tournament.standings.title')}: ${punteggio}`}
            aria-haspopup={isMobile ? 'dialog' : undefined}
            data-testid="tournament-game-open-standings"
          >
            {punteggio}
            <ChevronRight size={14} aria-hidden="true" className="tournament-game-icon" />
          </button>
        </>
      )}
      {/* Il pannello sta QUI e non in un altro slot: e' un portale su
          `document.body`, quindi la sua posizione nell'albero non mette niente
          dentro la riga, e sta accanto al bottone che lo apre — dove vive
          `classificaAperta`. Su desktop non si monta affatto. */}
      {classificaAperta && isMobile && (
        <FullscreenPanel
          title={t('tournament.standings.title')}
          closeLabel={t('common.close')}
          onClose={() => setClassificaAperta(false)}
        >
          {classifica}
        </FullscreenPanel>
      )}
    </>
  );

  // The tournament board lives in the tournament store, so the move list has to
  // be fed explicitly — left to itself it would read the (empty) multiplayer
  // store. The variant comes from the board the server sent, so the notation
  // matches the tournament's own variant.
  const moveHistory = boardState?.moveHistory;
  const notationVariant = boardState?.variant
    ?? (currentTournament?.variant ? toGameVariant(currentTournament.variant) : undefined);

  const timeControlLabel = !currentTournament?.timeControl
    ? undefined
    : currentTournament.timeControl === 'none'
      ? t('game.timeControls.noTimeLimit')
      : resolveTimeControl(currentTournament.timeControl)?.name;

  /* Per l'intestazione della scheda Mosse: il numero solo se viene davvero da
     una tabella (vedi `ballotNumber`). */
  const numeroInLista = aperturaDellaPartita ? ballotNumber(aperturaDellaPartita) : undefined;
  const aperturaInLista = useMemo(() => (aperturaDellaPartita
    ? {
        ...(numeroInLista !== undefined ? { id: numeroInLista } : {}),
        moves: aperturaDellaPartita.moves,
        ...(aperturaDellaPartita.forza ? { forza: aperturaDellaPartita.forza } : {}),
      }
    : null), [aperturaDellaPartita, numeroInLista]);

  /* UN'ESPRESSIONE SOLA per le due destinazioni: la barra mobile (via GameScreen) e la
     colonna (via GameSidebarTabs). Il torneo e' l'unico che non tiene il conto in uno
     store, e scritto due volte in linea finirebbe per divergere. `aperturaInLista.moves`
     e' lo stesso array, quindi lista e bottone non possono litigare. */
  const semimosseApertura = aperturaDellaPartita?.moves.length ?? 0;

  const mobilePanels: MobilePanel[] = useMemo(() => [
    {
      id: 'moves',
      label: t('game.sidebar.moves'),
      icon: <List size={20} aria-hidden="true" />,
      render: () => <MoveList moves={moveHistory} variant={notationVariant} opening={aperturaInLista} />,
    },
    {
      id: 'chat',
      label: t('game.sidebar.chat'),
      icon: <MessageSquare size={20} aria-hidden="true" />,
      badge: unreadCount,
      onOpen: () => setChatActive(true),
      onClose: () => setChatActive(false),
      render: () => <ChatPanel />,
    },
    /* L'occhio sta a destra della Chat, e c'è anche quando non guarda nessuno:
       sapere di NON essere guardati è un'informazione quanto il contrario. Di
       sola icona perché a 360px la barra non regge una terza parola. */
    {
      id: 'spectators',
      label: t('game.sidebar.spectators'),
      icon: <Eye size={20} aria-hidden="true" />,
      badge: spectatorCount,
      compact: true,
      render: () => <SpectatorsPanel />,
    },
  ], [t, moveHistory, notationVariant, aperturaInLista, unreadCount, spectatorCount, setChatActive]);

  // Result type for GameEndOverlay
  const resultType: 'win' | 'loss' | 'draw' = isWin ? 'win' : isDraw ? 'draw' : 'loss';

  // Overlay slot: post-game result overlay
  const overlayContent = showResultOverlay && gameResult !== null ? (
    <GameEndOverlay
      resultType={resultType}
      reasonText={endReasonText(t, gameEndReason, {
        loser: gameResult === 'white' ? Color.BLACK : gameResult === 'black' ? Color.WHITE : undefined,
        ruleMode: boardState?.ruleMode,
      })}
      opponentUsername={opponent?.username || t('common.anonymous')}
      opponentRating={opponent?.rating}
      opponentAvatar={opponent?.avatar}
      statsSlot={
        <>
          <div className="tge-points-container">
            <span className={`tge-points-big ${getResultClass()}`}>
              +{getPointsEarned()}
            </span>
          </div>

          {myRank && (
            <div className="tge-rank">
              {t('tournament.postGame.currentRank')}: #{myRank}
            </div>
          )}

          {ratingChange && (() => {
            const delta = ratingChange.newRating - ratingChange.oldRating;
            return (
              <div className="tge-rating-change">
                <span className="tge-rating-old">{ratingChange.oldRating}</span>
                <span className="tge-rating-arrow">{'\u2192'}</span>
                <span className="tge-rating-new">{ratingChange.newRating}</span>
                <span className={`tge-rating-delta ${delta > 0 ? 'positive' : delta < 0 ? 'negative' : ''}`}>
                  ({delta > 0 ? '+' : ''}{delta})
                </span>
              </div>
            );
          })()}
        </>
      }
      countdownSlot={
        /* Nel torneo svizzero il prossimo turno lo apre l'arbitro, e dirlo
           serve. Fuori dallo svizzero qui c'era solo il numero che scendeva:
           tolto quello, non resta niente da scrivere. */
        aTurni ? (
          <div className="tge-countdown">{t('tournament.swiss.waitingForRoundEnd')}</div>
        ) : null
      }
      actionsSlot={
        <button
          className="tge-continue-btn"
          onClick={handleContinue}
          type="button"
        >
          {t('tournament.postGame.continue')}
        </button>
      }
    />
  ) : undefined;

  /* Memoizzati: scendono a `GameScreen`, che e' memoizzato, e questo componente
     si ri-renderizza a ogni tick dell'orologio. Senza, il `memo` e' peso morto —
     stessa cura di `OnlineGame` e `LocalGame`. */
  const boardOverlaySlot = useMemo(() => (
        /*
          L'errore di partita, che qui non si vedeva.
          `AvvisoErrore` era montato SOLO in `OnlineGame` (grep in tutto `src`: la
          definizione e quell'unico montaggio), quindi in torneo `error` era scritto
          e mai letto — nemmeno il riavvio del server, che arriva a tutta la stanza.
        */
        <>
          {/*
            L'apertura sorteggiata e la manche, che in torneo non si vedevano.
            `DrawnOpeningBanner` era montato solo in `OnlineGame`, `LocalGame` e
            `ArenaGame`: in un torneo l'unico annuncio era l'overlay «trovato
            avversario», che lo svizzero spesso SALTA (`setGameStart` passa
            dritto a `playing`) e che il RITORNO non mostra affatto, perche'
            parte da solo. Risultato: l'apertura rigiocata — la cosa su cui si
            regge il fatto che il torneo resti valutato — non compariva da
            nessuna parte.
          */}
          <DrawnOpeningBanner
            opening={aperturaDellaPartita}
            triggerKey={null}
            /* Accanto all'«apertura sorteggiata» basta «1/2»; da solo — turno a
               mossa libera, dove il banner non ha altro da dire — quel numero
               non si capirebbe, e serve la frase intera. Stessa scelta di
               `OnlineGame`, stesse chiavi. */
            legLabel={
              mancheDellaPartita && mancheTotaliDellaPartita
                ? t(aperturaDellaPartita ? 'online.match.legShort' : 'online.match.legOf',
                    { leg: mancheDellaPartita, total: mancheTotaliDellaPartita })
                : undefined
            }
          />
          <AvvisoErrore errore={error} onChiudi={() => setError(null)} />
        </>
  ), [aperturaDellaPartita, mancheDellaPartita, mancheTotaliDellaPartita, t, error, setError]);

  const sidebarSlot = useMemo(() => (
        <GameSidebarTabs
          moves={moveHistory}
          variant={notationVariant}
          opening={aperturaInLista}
          openingPlyCount={semimosseApertura}
          standings={classifica}
          standingsRequest={richiestaClassifica}
        />
  ), [moveHistory, notationVariant, aperturaInLista, semimosseApertura, classifica, richiestaClassifica]);

  return (
    <GameScreen
      boardState={boardState}
      playerColor={color}
      /*
       * Le semi-mosse dell'apertura sorteggiata non sono mosse di nessuno.
       *
       * Qui mancava, e in un torneo con apertura — la configurazione tipica della
       * dama italiana — la damiera parte con tre semi-mosse gia' scritte: chi le
       * conta come mosse giocate crede che tutt'e due i giocatori abbiano gia'
       * mosso prima del primo tocco. Se ne accorge il saluto d'apertura, che non
       * sarebbe mai comparso. Il conto della prima mossa resta invece spento in
       * torneo (`primaMossaScadeAt` non arriva), quindi questa riga serve a quello.
       *
       * Dall'apertura DELLA PARTITA e non da `matchFoundData`, che si azzera con
       * l'overlay: e' lo stesso campo che alimenta il banner qui sotto.
       */
      openingPlyCount={semimosseApertura}
      isMyTurn={isOurTurn}
      isGameOver={isGameOver}
      flipped={color === 'black'}
      onMove={handleMove}
      allowPremove
      clockState={clockState}
      timeControlPreset={clockTimeControl}
      isPlaying={status === 'playing'}
      boardInteractive={status === 'playing'}
      tickClock={tickClock}
      topPlayer={topPlayer}
      bottomPlayer={bottomPlayer}
      turnText={getTurnText()}
      resultClass={getResultClass()}
      matchFoundData={matchOverlay}
      onMatchCountdownEnd={confirmMatch}
      headerCells={celleTorneo}
      overlaySlot={overlayContent}
      boardOverlaySlot={boardOverlaySlot}
      sidebarSlot={sidebarSlot}
      mobilePanels={mobilePanels}
      variant={notationVariant ? (notationVariant.toLowerCase() as 'international' | 'italian' | 'english') : undefined}
      timeControlLabel={timeControlLabel}
      isRated={currentTournament?.isRated}
      ruleMode={boardState?.ruleMode}
      onResign={handleResign}
      isOpponentDisconnected={isOpponentDisconnected}
      graceTimeRemaining={graceTimeRemaining}
      opponentMaiArrivato={opponentMaiArrivato}
      opponentDecideLOrologio={opponentDecideLOrologio}
      onOfferDraw={offerDraw}
      onAcceptDraw={acceptDraw}
      onDeclineDraw={declineDrawAction}
      drawOffered={drawOffered}
      opponentOfferedDraw={opponentOfferedDraw}
      /*
       * La pastiglia dice solo il RIFIUTO, come prima: cosa fare lo dice il
       * bottone, che e' due centimetri sotto e nello stesso sguardo («Muovi
       * prima»). Concatenare le due frasi dava una pastiglia di cinque righe a
       * 360px che ripeteva «patta» tre volte — e un trattino che incolla due
       * frasi e' una traduzione che non regge in otto lingue.
       */
      offerNotice={drawDeclinedNotice ? t('game.actions.drawDeclined') : null}
      drawBlockedLabel={pattaBloccata ? t(pattaBloccata.label) : null}
      drawBlockedReason={pattaBloccata ? t(pattaBloccata.reason) : null}
    />
  );
}

export default TournamentGame;
